import React, { useEffect, useState, useCallback } from 'react';
import packagingService from '../services/packagingService';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmModal from '../components/ConfirmModal';
import PackagingPhysicalCountModal from '../components/PackagingPhysicalCountModal';
import { usePermissions } from '../context/PermissionContext';

const fmt = (n, decimals = 2) => n != null ? `$${parseFloat(n).toFixed(decimals)}` : '—';
const fmtQty = (n, uom) => n != null ? `${parseFloat(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${uom || ''}`.trim() : '—';
const fmtDate = (d) => d ? new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const REASON_LABELS = { count: 'Stock Count', waste: 'Waste / Write-Off', correction: 'Correction', other: 'Other' };
const CATEGORY_OPTIONS = ['Box', 'Bag', 'Label', 'Sleeve', 'Sticker', 'Twist Tie', 'Wrap', 'Container', 'Lid', 'Other'];

// Evaluate simple math expressions on blur (e.g. "10*5", "100/4", "2+3")
function evalMath(raw) {
  if (raw === '' || raw == null) return raw;
  const str = String(raw).trim();
  if (/^[0-9+\-*/().\s]+$/.test(str)) {
    try {
      // eslint-disable-next-line no-new-func
      const result = Function('"use strict"; return (' + str + ')')();
      if (typeof result === 'number' && isFinite(result)) return String(Math.round(result * 10000) / 10000);
    } catch { /* leave as-is */ }
  }
  return str;
}

// Number input that evaluates math on blur
function MathInput({ value, onChange, ...props }) {
  return (
    <input
      {...props}
      type="text"
      inputMode="decimal"
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={e => {
        const evaled = evalMath(e.target.value);
        if (evaled !== e.target.value) onChange(evaled);
      }}
    />
  );
}

// ── Packaging Material Modal ──────────────────────────────────────────────────

function PackagingMaterialModal({ material, onSave, onClose }) {
  const [form, setForm] = useState({
    name: material?.name || '',
    sku: material?.sku || '',
    category: material?.category || '',
    unitOfMeasure: material?.unitOfMeasure || '',
    unitsPerCase: material?.unitsPerCase || '',
    defaultSupplier: material?.defaultSupplier || '',
    reorderThreshold: material?.reorderThreshold || '',
    notes: material?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.unitOfMeasure.trim()) {
      setError('Name and unit of measure are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        unitsPerCase: form.unitsPerCase !== '' ? parseInt(form.unitsPerCase) : null,
        reorderThreshold: form.reorderThreshold !== '' ? parseFloat(form.reorderThreshold) : null,
      };
      const result = material?.id
        ? await packagingService.updateMaterial(material.id, payload)
        : await packagingService.createMaterial(payload);
      onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{material?.id ? 'Edit' : 'New'} Packaging Material</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Name <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" value={form.name} onChange={e => set('name', e.target.value)} placeholder='e.g. 12" Pizza Box' />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>SKU</label>
                  <input className="form-control form-control-sm" value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="Internal / supplier SKU" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Category</label>
                  <SearchableSelect
                    options={CATEGORY_OPTIONS.map(c => ({ value: c, label: c }))}
                    value={form.category}
                    onChange={val => set('category', val || '')}
                    placeholder="Select a category"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Unit of Measure <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" value={form.unitOfMeasure} onChange={e => set('unitOfMeasure', e.target.value)} placeholder="each, roll, sheet…" />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Units per Case</label>
                  <MathInput className="form-control form-control-sm" value={form.unitsPerCase} onChange={v => set('unitsPerCase', v)} placeholder="e.g. 50" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Default Supplier</label>
                  <input className="form-control form-control-sm" value={form.defaultSupplier} onChange={e => set('defaultSupplier', e.target.value)} placeholder="Supplier name" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Reorder Threshold</label>
                  <div className="input-group input-group-sm">
                    <MathInput className="form-control" value={form.reorderThreshold} onChange={v => set('reorderThreshold', v)} placeholder="Alert when below" />
                    <span className="input-group-text" style={{ fontSize: 12 }}>{form.unitOfMeasure || 'units'}</span>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                {material?.id ? 'Save Changes' : 'Create Material'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Receiving Modal ───────────────────────────────────────────────────────────

function PackagingReceivingModal({ materials, preselectedMaterial, onSave, onClose }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    packagingMaterialId: preselectedMaterial ? String(preselectedMaterial.id) : '',
    receivedDate: today,
    supplierName: preselectedMaterial?.defaultSupplier || '',
    quantity: '',
    unitCost: '',
    invoiceRef: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedMat = materials.find(m => String(m.id) === form.packagingMaterialId);
  const qty = parseFloat(form.quantity);
  const cost = parseFloat(form.unitCost);
  const total = !isNaN(qty) && !isNaN(cost) ? (qty * cost).toFixed(2) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.packagingMaterialId || !form.receivedDate || !form.quantity) {
      setError('Material, date, and quantity are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      await packagingService.createReceiving({
        packagingMaterialId: parseInt(form.packagingMaterialId),
        receivedDate: form.receivedDate,
        supplierName: form.supplierName || null,
        quantity: parseFloat(form.quantity),
        unitCost: form.unitCost !== '' ? parseFloat(form.unitCost) : null,
        totalCost: total ? parseFloat(total) : null,
        invoiceRef: form.invoiceRef || null,
        notes: form.notes || null,
      });
      onSave();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Log Receiving</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-md-8">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Material <span className="text-danger">*</span></label>
                  <SearchableSelect
                    options={materials.map(m => ({ value: String(m.id), label: `${m.name}${m.category ? ` — ${m.category}` : ''}` }))}
                    value={form.packagingMaterialId}
                    onChange={val => {
                      const mat = materials.find(m => String(m.id) === val);
                      set('packagingMaterialId', val || '');
                      if (mat?.defaultSupplier && !form.supplierName) set('supplierName', mat.defaultSupplier);
                    }}
                    placeholder="Select packaging material"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Received Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.receivedDate} onChange={e => set('receivedDate', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Supplier</label>
                  <input className="form-control form-control-sm" value={form.supplierName} onChange={e => set('supplierName', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Invoice / Reference #</label>
                  <input className="form-control form-control-sm" value={form.invoiceRef} onChange={e => set('invoiceRef', e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Quantity <span className="text-danger">*</span></label>
                  <div className="input-group input-group-sm">
                    <MathInput className="form-control" value={form.quantity} onChange={v => set('quantity', v)} placeholder="e.g. 100" />
                    {selectedMat && <span className="input-group-text" style={{ fontSize: 12 }}>{selectedMat.unitOfMeasure}</span>}
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Unit Cost</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">$</span>
                    <MathInput className="form-control" value={form.unitCost} onChange={v => set('unitCost', v)} placeholder="0.0000" />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label" style={{ fontSize: 13, color: '#6c757d' }}>Total Cost (auto)</label>
                  <div className="form-control form-control-sm bg-light" style={{ color: total ? '#198754' : '#aaa', fontWeight: total ? 700 : 400 }}>
                    {total ? `$${total}` : '—'}
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                Log Receiving
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Adjustment Modal ──────────────────────────────────────────────────────────

function PackagingAdjustmentModal({ materials, preselectedMaterial, onSave, onClose }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    packagingMaterialId: preselectedMaterial ? String(preselectedMaterial.id) : '',
    adjustmentDate: today,
    quantityDelta: '',
    reason: 'count',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedMat = materials.find(m => String(m.id) === form.packagingMaterialId);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.packagingMaterialId || !form.adjustmentDate || form.quantityDelta === '') {
      setError('Material, date, and quantity are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      await packagingService.createAdjustment({
        packagingMaterialId: parseInt(form.packagingMaterialId),
        adjustmentDate: form.adjustmentDate,
        quantityDelta: parseFloat(form.quantityDelta),
        reason: form.reason || 'count',
        notes: form.notes || null,
      });
      onSave();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Adjust Stock</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              {preselectedMaterial && (
                <div className="alert alert-light py-2 mb-3" style={{ fontSize: 13 }}>
                  <strong>{preselectedMaterial.name}</strong>
                  {preselectedMaterial.currentBalance != null && (
                    <span className="ms-2 text-muted">Current balance: {fmtQty(preselectedMaterial.currentBalance, preselectedMaterial.unitOfMeasure)}</span>
                  )}
                </div>
              )}
              <div className="row g-3">
                {!preselectedMaterial && (
                  <div className="col-12">
                    <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Material <span className="text-danger">*</span></label>
                    <SearchableSelect
                      options={materials.map(m => ({ value: String(m.id), label: m.name }))}
                      value={form.packagingMaterialId}
                      onChange={val => set('packagingMaterialId', val || '')}
                      placeholder="Select material"
                    />
                  </div>
                )}
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.adjustmentDate} onChange={e => set('adjustmentDate', e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Reason</label>
                  <select className="form-select form-select-sm" value={form.reason} onChange={e => set('reason', e.target.value)}>
                    {Object.entries(REASON_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Quantity Delta <span className="text-danger">*</span>
                    <span className="fw-normal text-muted ms-1" style={{ fontSize: 12 }}>(positive = add, negative = remove)</span>
                  </label>
                  <div className="input-group input-group-sm">
                    <MathInput className="form-control" value={form.quantityDelta} onChange={v => set('quantityDelta', v)} placeholder="e.g. -10 or 25" />
                    {(selectedMat || preselectedMaterial) && (
                      <span className="input-group-text" style={{ fontSize: 12 }}>{(selectedMat || preselectedMaterial).unitOfMeasure}</span>
                    )}
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes</label>
                  <textarea className="form-control form-control-sm" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-warning" disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                Save Adjustment
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Add Cost Entry Modal ──────────────────────────────────────────────────────

function AddCostEntryModal({ materialId, onSave, onClose }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ costPerUnit: '', effectiveDate: today, note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.costPerUnit || !form.effectiveDate) { setError('Cost and effective date are required.'); return; }
    setSaving(true); setError('');
    try {
      const result = await packagingService.addCostEntry(materialId, {
        costPerUnit: parseFloat(form.costPerUnit),
        effectiveDate: form.effectiveDate,
        note: form.note || null,
      });
      onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Cost Entry</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Cost per Unit <span className="text-danger">*</span></label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">$</span>
                    <MathInput className="form-control" value={form.costPerUnit} onChange={v => set('costPerUnit', v)} placeholder="0.0000" />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Effective Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Note</label>
                  <input className="form-control form-control-sm" value={form.note} onChange={e => set('note', e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                Add Entry
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Inventory Tab ─────────────────────────────────────────────────────────────

function InventoryTab({ materials, loading, onEditMaterial, onAdjust, onReceive, onDeleteMaterial }) {
  const totalValue = materials.reduce((sum, m) => sum + (m.totalStockValue || 0), 0);
  const belowCount = materials.filter(m => m.belowThreshold).length;

  return (
    <div>
      {belowCount > 0 && (
        <div className="alert alert-warning py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
          <span>⚠️</span>
          <span><strong>{belowCount} material{belowCount > 1 ? 's' : ''}</strong> below reorder threshold.</span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><span className="spinner-border" /></div>
      ) : materials.length === 0 ? (
        <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>
          No packaging materials yet. Click "+ New Material" to add one.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ fontSize: 13 }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th>Material</th>
                <th>Category</th>
                <th className="text-end">Balance</th>
                <th>Reorder Level</th>
                <th className="text-end">Cost / Unit</th>
                <th className="text-end">Stock Value</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials.map(m => (
                <tr key={m.id} style={m.belowThreshold ? { background: '#fff8e1' } : {}}>
                  <td>
                    <div className="fw-semibold">{m.name}</div>
                    {m.sku && <div className="text-muted" style={{ fontSize: 11 }}>SKU: {m.sku}</div>}
                    {m.defaultSupplier && <div className="text-muted" style={{ fontSize: 11 }}>{m.defaultSupplier}</div>}
                  </td>
                  <td>{m.category ? <span className="badge bg-light text-dark border">{m.category}</span> : <span className="text-muted">—</span>}</td>
                  <td className="text-end fw-semibold">{fmtQty(m.currentBalance, m.unitOfMeasure)}</td>
                  <td>
                    {m.reorderThreshold != null ? (
                      <div>
                        <div className="progress" style={{ height: 6, width: 80 }}>
                          <div
                            className={`progress-bar ${m.belowThreshold ? 'bg-warning' : 'bg-success'}`}
                            style={{ width: `${Math.min(100, (m.currentBalance / parseFloat(m.reorderThreshold)) * 100)}%` }}
                          />
                        </div>
                        <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2 }}>
                          {m.belowThreshold ? '⚠️ ' : ''}{fmtQty(m.reorderThreshold, m.unitOfMeasure)}
                        </div>
                      </div>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-end">{fmt(m.currentCostPerUnit)}</td>
                  <td className="text-end fw-semibold">{m.totalStockValue != null ? fmt(m.totalStockValue) : '—'}</td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end flex-wrap">
                      <button className="btn btn-outline-success btn-sm" onClick={() => onReceive(m)}>+ Receive</button>
                      <button className="btn btn-outline-warning btn-sm" onClick={() => onAdjust(m)}>Adjust</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => onEditMaterial(m)}>✏️</button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => onDeleteMaterial(m)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #dee2e6' }}>
                <td colSpan={5} className="fw-bold" style={{ fontSize: 13 }}>Total Packaging Inventory Value</td>
                <td className="text-end fw-bold" style={{ color: '#198754', fontSize: 14 }}>{fmt(totalValue)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Receiving Log Tab ─────────────────────────────────────────────────────────

function ReceivingTab({ materials, onNewReceiving }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMat, setFilterMat] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMat) params.materialId = filterMat;
      if (dateFrom)  params.dateFrom   = dateFrom;
      if (dateTo)    params.dateTo     = dateTo;
      setLogs(await packagingService.getReceiving(params));
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filterMat, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await packagingService.deleteReceiving(toDelete.id);
      setLogs(prev => prev.filter(l => l.id !== toDelete.id));
      setToDelete(null);
    } catch { /* ignore */ } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="d-flex gap-2 mb-3 flex-wrap align-items-end">
        <div style={{ minWidth: 200 }}>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>Material</label>
          <SearchableSelect
            options={[{ value: '', label: 'All materials' }, ...materials.map(m => ({ value: String(m.id), label: m.name }))]}
            value={filterMat}
            onChange={val => setFilterMat(val || '')}
            placeholder="All materials"
          />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>From</label>
          <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>To</label>
          <input type="date" className="form-control form-control-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button className="btn btn-outline-secondary btn-sm align-self-end" onClick={() => { setFilterMat(''); setDateFrom(''); setDateTo(''); }}>Clear</button>
        <button className="btn btn-success btn-sm align-self-end ms-auto" onClick={onNewReceiving}>+ New Receiving</button>
      </div>

      {loading ? (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No receiving logs found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle" style={{ fontSize: 13 }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th>Date</th>
                <th>Material</th>
                <th>Supplier</th>
                <th className="text-end">Quantity</th>
                <th className="text-end">Unit Cost</th>
                <th className="text-end">Total</th>
                <th>Ref #</th>
                <th>Received By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(log.receivedDate)}</td>
                  <td className="fw-semibold">{log.Material?.name || '—'}</td>
                  <td>{log.supplierName || '—'}</td>
                  <td className="text-end">{fmtQty(log.quantity, log.Material?.unitOfMeasure)}</td>
                  <td className="text-end">{log.unitCost ? fmt(log.unitCost, 4) : '—'}</td>
                  <td className="text-end fw-semibold">{log.totalCost ? fmt(log.totalCost) : '—'}</td>
                  <td>{log.invoiceRef || '—'}</td>
                  <td>{log.ReceivedBy ? `${log.ReceivedBy.firstName} ${log.ReceivedBy.lastName}` : '—'}</td>
                  <td>
                    <button className="btn btn-outline-danger btn-sm px-2" onClick={() => setToDelete(log)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toDelete && (
        <ConfirmModal
          message={`Delete this receiving log for ${toDelete.Material?.name || 'this material'} on ${fmtDate(toDelete.receivedDate)}? This will affect the current balance.`}
          onConfirm={doDelete}
          onCancel={() => setToDelete(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}

// ── Cost History Tab ──────────────────────────────────────────────────────────

function CostHistoryTab({ materials }) {
  const [selectedId, setSelectedId] = useState('');
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    if (!selectedId) { setCosts([]); return; }
    setLoading(true);
    packagingService.getMaterialCosts(selectedId)
      .then(setCosts)
      .catch(() => setCosts([]))
      .finally(() => setLoading(false));
  }, [selectedId]);

  return (
    <div>
      <div className="d-flex gap-2 mb-3 align-items-end flex-wrap">
        <div style={{ minWidth: 260 }}>
          <label className="form-label mb-1" style={{ fontSize: 12 }}>Select Material</label>
          <SearchableSelect
            options={materials.map(m => ({ value: String(m.id), label: m.name }))}
            value={selectedId}
            onChange={val => setSelectedId(val || '')}
            placeholder="Select a material to view cost history"
          />
        </div>
        {selectedId && (
          <button className="btn btn-outline-primary btn-sm align-self-end" onClick={() => setShowAdd(true)}>+ Add Cost Entry</button>
        )}
      </div>

      {!selectedId && (
        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>Select a material above to view its cost history.</div>
      )}
      {selectedId && loading && (
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
      )}
      {selectedId && !loading && costs.length === 0 && (
        <div className="text-center text-muted py-4" style={{ fontSize: 14 }}>No cost entries yet.</div>
      )}
      {selectedId && !loading && costs.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm align-middle" style={{ fontSize: 13 }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th>Effective Date</th>
                <th className="text-end">Cost / Unit</th>
                <th>Source</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {costs.map((c, i) => (
                <tr key={c.id} style={i === 0 ? { background: '#f0fff4' } : {}}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {fmtDate(c.effectiveDate)}
                    {i === 0 && <span className="badge bg-success ms-2" style={{ fontSize: 10 }}>Current</span>}
                  </td>
                  <td className="text-end fw-semibold">{fmt(c.costPerUnit, 4)}</td>
                  <td>
                    <span className={`badge ${c.source === 'receiving' ? 'bg-info text-dark' : 'bg-secondary'}`} style={{ fontSize: 10 }}>
                      {c.source === 'receiving' ? 'From Receiving' : 'Manual'}
                    </span>
                  </td>
                  <td>{c.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && selectedId && (
        <AddCostEntryModal
          materialId={selectedId}
          onSave={(entry) => { setCosts(prev => [entry, ...prev]); setShowAdd(false); }}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}

function PackagingPhysicalCountHistoryTab({ sessions, loading, isAdmin, expandedId, expandedDetail, onToggleExpand, onDelete, onStart }) {
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          Reconcile packaging stock against a real physical count — weekly, monthly, or whenever
          you want. Any deviation auto-creates a tracked adjustment with a reason, credited to you.
        </span>
        <button className="btn btn-primary btn-sm" onClick={onStart}>▶ Start New Count</button>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Performed By</th>
                <th>Items Counted</th>
                <th>Adjustments</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">No physical counts recorded yet.</td>
                </tr>
              ) : (
                sessions.map(s => (
                  <React.Fragment key={s.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => onToggleExpand(s.id)}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(s.countedAt)}</td>
                      <td>{s.performedByName || '—'}</td>
                      <td>{s.lineCount}</td>
                      <td>
                        {s.adjustmentCount > 0
                          ? <span className="badge bg-warning text-dark">{s.adjustmentCount} adjustment{s.adjustmentCount === 1 ? '' : 's'}</span>
                          : <span className="badge bg-success">All matched</span>}
                      </td>
                      <td>{s.notes || <span className="text-muted">—</span>}</td>
                      <td className="text-end">
                        <span className="me-2">{expandedId === s.id ? '▲' : '▼'}</span>
                        {isAdmin && (
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={(e) => { e.stopPropagation(); onDelete(s); }}
                            title="Delete (admin only) — reverses its adjustments"
                          >
                            Del
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === s.id && (
                      <tr>
                        <td colSpan={6} className="bg-light">
                          {!expandedDetail ? (
                            <div className="text-center py-2"><div className="spinner-border spinner-border-sm text-primary" /></div>
                          ) : (
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr><th>Material</th><th>System</th><th>Counted</th><th>Delta</th></tr>
                              </thead>
                              <tbody>
                                {(expandedDetail.lines || []).map(line => (
                                  <tr key={line.id}>
                                    <td>{line.Material?.name || line.packagingMaterialId}</td>
                                    <td>{Number(line.systemBalance).toFixed(2)}</td>
                                    <td>{Number(line.countedQty).toFixed(2)}</td>
                                    <td className={Number(line.delta) === 0 ? 'text-muted' : Number(line.delta) > 0 ? 'text-success' : 'text-danger'}>
                                      {Number(line.delta) > 0 ? '+' : ''}{Number(line.delta).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PackagingInventoryPage() {
  const { role } = usePermissions();
  const isAdmin = role === 'admin' || role === 'superadmin';
  const [tab, setTab] = useState('inventory');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMaterial, setEditMaterial] = useState(null); // null = closed, false = new, obj = edit
  const [adjustMaterial, setAdjustMaterial] = useState(null);
  const [receiveMaterial, setReceiveMaterial] = useState(null); // null = closed, false/obj = open
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [physicalCounts, setPhysicalCounts] = useState([]);
  const [physicalCountsLoading, setPhysicalCountsLoading] = useState(false);
  const [showPhysicalCountModal, setShowPhysicalCountModal] = useState(false);
  const [expandedCountId, setExpandedCountId] = useState(null);
  const [expandedCountDetail, setExpandedCountDetail] = useState(null);
  const [countToDelete, setCountToDelete] = useState(null);
  const [deletingCount, setDeletingCount] = useState(false);

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    try {
      setMaterials(await packagingService.getMaterials());
    } catch {
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const loadPhysicalCounts = useCallback(async () => {
    setPhysicalCountsLoading(true);
    try {
      setPhysicalCounts(await packagingService.getPhysicalCounts());
    } catch {
      setPhysicalCounts([]);
    } finally {
      setPhysicalCountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'physicalCounts') loadPhysicalCounts();
  }, [tab, loadPhysicalCounts]);

  const handleMaterialSaved = () => {
    setEditMaterial(null);
    loadMaterials();
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await packagingService.deleteMaterial(toDelete.id);
      setMaterials(prev => prev.filter(m => m.id !== toDelete.id));
      setToDelete(null);
    } catch { /* ignore */ } finally {
      setDeleting(false);
    }
  };

  const handleToggleExpandCount = async (id) => {
    if (expandedCountId === id) { setExpandedCountId(null); setExpandedCountDetail(null); return; }
    setExpandedCountId(id);
    setExpandedCountDetail(null);
    try {
      setExpandedCountDetail(await packagingService.getPhysicalCount(id));
    } catch {
      setExpandedCountDetail({ lines: [] });
    }
  };

  const handleDeletePhysicalCount = async () => {
    if (!countToDelete) return;
    setDeletingCount(true);
    try {
      await packagingService.deletePhysicalCount(countToDelete.id);
      if (expandedCountId === countToDelete.id) { setExpandedCountId(null); setExpandedCountDetail(null); }
      setCountToDelete(null);
      loadPhysicalCounts();
      loadMaterials();
    } catch { /* ignore */ } finally {
      setDeletingCount(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0 fw-bold">📦 Packaging Inventory</h4>
        {tab === 'inventory' && (
          <button className="btn btn-primary btn-sm" onClick={() => setEditMaterial(false)}>+ New Material</button>
        )}
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'inventory' ? 'active' : ''}`} onClick={() => setTab('inventory')}>Inventory</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'receiving' ? 'active' : ''}`} onClick={() => setTab('receiving')}>Receiving Log</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'costs' ? 'active' : ''}`} onClick={() => setTab('costs')}>Cost History</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'physicalCounts' ? 'active' : ''}`} onClick={() => setTab('physicalCounts')}>📋 Physical Count</button>
        </li>
      </ul>

      {tab === 'inventory' && (
        <InventoryTab
          materials={materials}
          loading={loading}
          onEditMaterial={m => setEditMaterial(m)}
          onAdjust={m => setAdjustMaterial(m)}
          onReceive={m => setReceiveMaterial(m)}
          onDeleteMaterial={m => setToDelete(m)}
        />
      )}
      {tab === 'receiving' && (
        <ReceivingTab
          materials={materials}
          onNewReceiving={() => setReceiveMaterial(false)}
        />
      )}
      {tab === 'costs' && <CostHistoryTab materials={materials} />}
      {tab === 'physicalCounts' && (
        <PackagingPhysicalCountHistoryTab
          sessions={physicalCounts}
          loading={physicalCountsLoading}
          isAdmin={isAdmin}
          expandedId={expandedCountId}
          expandedDetail={expandedCountDetail}
          onToggleExpand={handleToggleExpandCount}
          onDelete={(s) => setCountToDelete(s)}
          onStart={() => setShowPhysicalCountModal(true)}
        />
      )}

      {showPhysicalCountModal && (
        <PackagingPhysicalCountModal
          onClose={() => setShowPhysicalCountModal(false)}
          onCompleted={() => { setShowPhysicalCountModal(false); loadPhysicalCounts(); loadMaterials(); }}
        />
      )}

      {editMaterial !== null && (
        <PackagingMaterialModal
          material={editMaterial || null}
          onSave={handleMaterialSaved}
          onClose={() => setEditMaterial(null)}
        />
      )}

      {adjustMaterial && (
        <PackagingAdjustmentModal
          materials={materials}
          preselectedMaterial={adjustMaterial}
          onSave={() => { setAdjustMaterial(null); loadMaterials(); }}
          onClose={() => setAdjustMaterial(null)}
        />
      )}

      {receiveMaterial !== null && (
        <PackagingReceivingModal
          materials={materials}
          preselectedMaterial={receiveMaterial || null}
          onSave={() => { setReceiveMaterial(null); loadMaterials(); }}
          onClose={() => setReceiveMaterial(null)}
        />
      )}

      {toDelete && (
        <ConfirmModal
          message={`Deactivate "${toDelete.name}"? It will no longer appear in the inventory list.`}
          onConfirm={handleDelete}
          onCancel={() => setToDelete(null)}
          loading={deleting}
        />
      )}

      {countToDelete && (
        <ConfirmModal
          message="Delete this physical count? This will reverse every adjustment it created. This cannot be undone."
          onConfirm={handleDeletePhysicalCount}
          onCancel={() => setCountToDelete(null)}
          loading={deletingCount}
        />
      )}
    </div>
  );
}
